import { useState } from "react";
import {
  LayoutGrid, Loader2, AlertCircle, RefreshCw,
  Grid2x2, FileText, BookOpen, Settings, Building, Briefcase,
} from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "../ui/popover";
import type { SubApp } from "../../data/appShellTypes";
import { APP_NAV_CONFIGS } from "../../data/appNavConfig";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  "grid-2x2": Grid2x2,
  "file-text": FileText,
  "book-open": BookOpen,
  settings: Settings,
  building: Building,
  briefcase: Briefcase,
};

export interface AppSelectorProps {
  apps: SubApp[];
  currentApp: SubApp | null;
  onSwitch: (slug: string) => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Optional custom trigger element. When provided, replaces the default LayoutGrid icon button. */
  trigger?: React.ReactNode;
}

export default function AppSelector({
  apps,
  currentApp,
  onSwitch,
  isLoading = false,
  error = null,
  onRetry,
  trigger,
}: AppSelectorProps) {
  const [open, setOpen] = useState(false);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1" role="status" aria-label="Loading apps">
        <Loader2 className="size-3.5 animate-spin text-slate-400" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1" role="alert">
        <AlertCircle className="size-3.5 text-red-400 shrink-0" />
        <span className="text-[12px] text-red-500 truncate max-w-[120px]">{error}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center gap-1 px-1.5 py-0.5 text-[11px] text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors"
            aria-label="Retry loading apps"
          >
            <RefreshCw className="size-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <button
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-700 hover:bg-white/60 transition-colors"
            aria-label="Switch app"
          >
            <LayoutGrid className="size-5" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[280px] p-3"
      >
        <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-slate-100">
          <img src="/cortex-logo.png" alt="" className="h-6 rounded-md object-cover" />
          <span className="text-[13px] font-semibold text-slate-700 tracking-[0.01em]">Cortex</span>
        </div>
        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-2">
          Apps
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {[...apps].sort((a, b) => {
            const order: Record<string, number> = { studio: 0, docs: 1 };
            return (order[a.slug] ?? 99) - (order[b.slug] ?? 99);
          }).map((app) => {
            const isCurrent = app.slug === currentApp?.slug;
            const Icon = ICON_MAP[app.icon || ""] || Grid2x2;
            return (
              <button
                key={app.slug}
                onClick={() => { onSwitch(app.slug); setOpen(false); }}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-lg text-center transition-colors ${
                  isCurrent
                    ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <Icon className={`size-5 ${isCurrent ? "text-indigo-600" : "text-slate-400"}`} />
                <span className="text-[11px] font-medium leading-tight">
                  {APP_NAV_CONFIGS[app.slug]?.title?.replace(/^Cortex\s+/, "") || app.name}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
